import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ExternalServiceDialog } from "@/components/ExternalServiceDialog";
import {
  Save, Trash2, Plus, Calendar as CalendarIcon, Wrench, Bus, Clock, AlertTriangle,
  X, Loader2, Package, MapPin, User, Sparkles, ChevronDown, Search,
} from "lucide-react";

// ---------- Sabitler ----------
const JOB_LEVELS = ["Hafif İş", "Orta İş", "Ağır İş", "Kaporta/Boya", "Elektrik", "Diğer"];

const DURATION_BUCKETS = [
  { label: "15 Dk", minutes: 15 },
  { label: "30 Dk", minutes: 30 },
  { label: "45 Dk", minutes: 45 },
  { label: "1 Saat", minutes: 60 },
  { label: "1.5 Saat", minutes: 90 },
  { label: "2 Saat", minutes: 120 },
  { label: "3 Saat", minutes: 180 },
  { label: "4+ Saat", minutes: 240 },
];

const PARTS_CATALOG = [
  "Fren Balatası", "Kampana", "Fren Diski", "Fren Kliperi", "Fren Körüğü", "ABS Sensörü",
  "Poryo Bilyası", "Rulman", "Amortisör", "Rot Başı", "Salıncak", "Körük (Hava Yastığı)",
  "Hava Kurutucu Kartuş", "Hava Hortumu", "Ventil", "V Kayışı", "Alternatör", "Marş Motoru",
  "Akü", "Yağ Filtresi", "Mazot Filtresi", "Hava Filtresi", "Antifriz", "Radyatör Hortumu",
  "Termostat", "Su Pompası", "H7 Ampül", "Silecek Süpürgesi", "Klima Kompresörü", "Turbo",
  "Enjektör", "Yakıt Pompası",
];

const clsRed = "ring-2 ring-red-500 ring-offset-2 shake-once";
const clsAmber = "ring-2 ring-amber-500 ring-offset-2";

function todayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function buildTimeWindow(dateYmd, minutes) {
  const start = `${dateYmd}T09:00:00`;
  const endDate = new Date(new Date(`${dateYmd}T09:00:00`).getTime() + minutes * 60000);
  const hh = String(endDate.getHours()).padStart(2, "0");
  const mm = String(endDate.getMinutes()).padStart(2, "0");
  return { start_time: start, end_time: `${dateYmd}T${hh}:${mm}:00` };
}

const emptyItem = () => ({
  job_level: "",
  primary_mechanic_id: "",
  secondary_mechanic_id: "",
  fault_reported: "",
  action_taken: "",
  repair_status: "Başarılı Onarım",
  custom_points: "",
  // Update 20
  item_id: "",
  system_id: "",
  system_name: "",
  part_id: "",
  part_name: "",
  location: "Genel",
  locations: ["Genel"],
});

export default function DataEntryForm() {
  const nav = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [date, setDate] = useState(todayYmd());
  const [overtime, setOvertime] = useState(false);
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [durationBucket, setDurationBucket] = useState("1 Saat");
  const [plate, setPlate] = useState("");
  const [vehicleCode, setVehicleCode] = useState("");
  const [items, setItems] = useState([emptyItem()]);
  const [parts, setParts] = useState([]);
  const [driverFault, setDriverFault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [mechanics, setMechanics] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [systems, setSystems] = useState([]);
  const [locations, setLocations] = useState(["Genel"]);
  const [repeats, setRepeats] = useState({});   // { [itemIdx]: {severity, previous, decision} }

  // Update 29.20 — Usta listesi: /mechanics × /personnel role_type=Usta
  // kanonik eşleşmesi. Aşamalı sync; mevcut iş emri primary_mechanic_id
  // referansları (mechanics.id) kırılmaz.
  useEffect(() => {
    const norm = (s) => (s || "").toString().toLocaleLowerCase("tr-TR").replace(/[^a-z0-9]/gi, "");
    Promise.all([
      api.get("/mechanics").catch(() => ({ data: [] })),
      api.get("/personnel").catch(() => ({ data: [] })),
    ]).then(([mr, pr]) => {
      const mechs = (mr.data || []).filter((m) => m.status !== "pasif");
      const ustaNames = new Set(
        (pr.data || [])
          .filter((p) => (p.role_type || "") === "Usta")
          .map((p) => norm(p.name))
      );
      // Yalnız personel.role_type=Usta ile eşleşen mechanics'i göster
      setMechanics(mechs.filter((m) => ustaNames.has(norm(m.name))));
    });
    api.get("/vehicles").then((r) => setVehicles((r.data || []).filter((v) => v.active !== false))).catch(() => {});
    api.get("/vehicle-sections").then((r) => {
      setSystems(r.data.systems || []);
      setLocations(r.data.locations || ["Genel"]);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/work-orders/${id}`).then((r) => {
      const wo = r.data;
      setDate((wo.date || "").slice(0, 10) || todayYmd());
      setOvertime(!!wo.breakdown_or_overtime || !!wo.overtime);
      setDurationMinutes(wo.duration_minutes || 60);
      const bucket = DURATION_BUCKETS.find((b) => b.minutes === wo.duration_minutes);
      setDurationBucket(bucket?.label || wo.duration_bucket || "1 Saat");
      setPlate(wo.vehicle_plate || "");
      setVehicleCode(wo.vehicle_code || "");
      setDriverFault(!!wo.driver_fault);
      setItems((wo.items && wo.items.length ? wo.items : [emptyItem()]).map((it) => ({
        job_level: it.job_level || "",
        primary_mechanic_id: it.primary_mechanic_id || "",
        secondary_mechanic_id: it.secondary_mechanic_id || "",
        fault_reported: it.fault_reported || "",
        action_taken: it.action_taken || "",
        repair_status: it.repair_status || "Başarılı Onarım",
        custom_points: it.custom_points ?? "",
        item_id: it.item_id || "",
        system_id: it.system_id || "",
        system_name: it.system_name || "",
        part_id: it.part_id || "",
        part_name: it.part_name || "",
        location: it.location || "Genel",
        locations: (Array.isArray(it.locations) && it.locations.length ? it.locations : [it.location || "Genel"]),
      })));
      setParts((wo.parts_list || []).map((p) => ({ name: p.name || "", quantity: p.quantity || 1, note: p.note || "" })));
    }).catch(() => toast.error("İş emri yüklenemedi"));
  }, [id, isEdit]);

  const checkRepeat = useCallback(async (idx, item) => {
    if (!plate.trim() || !item.system_id) {
      setRepeats((r) => ({ ...r, [idx]: null }));
      return;
    }
    try {
      const locsCsv = (item.locations && item.locations.length ? item.locations : [item.location || "Genel"]).join(",");
      const { data } = await api.get("/work-orders/repeat-check", {
        params: {
          plate: plate.trim(),
          system_id: item.system_id,
          part_id: item.part_id || undefined,
          locations: locsCsv,
          ...(isEdit ? { exclude_id: id } : {}),
        },
      });
      // Update 29 — Her önceki kalem için ayrı karar bloğu (per candidate)
      setRepeats((r) => {
        const prevDecisions = (r[idx]?.decisions) || {};
        const decisions = {};
        (data.candidates || []).forEach((c) => {
          const k = `${c.work_order_id}::${c.item_id}`;
          decisions[k] = prevDecisions[k] || null;
        });
        return { ...r, [idx]: { ...data, decisions } };
      });
    } catch { /* silent */ }
  }, [plate, isEdit, id]);

  useEffect(() => {
    items.forEach((it, i) => { if (it.system_id) checkRepeat(i, it); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plate]);

  const updateItem = (idx, patch) => {
    let next = { ...items[idx], ...patch };
    if ("system_id" in patch) next.part_id = "";
    // Update 29 — çoklu konum + Genel exclusivity
    if ("locations" in patch) {
      let locs = patch.locations || [];
      // Yeni "Genel" eklendiyse diğerlerini temizle
      const prevLocs = items[idx].locations || [];
      if (locs.includes("Genel") && !prevLocs.includes("Genel")) {
        locs = ["Genel"];
      } else if (locs.length > 1 && locs.includes("Genel")) {
        // Başka konum eklendiyse Genel'i çıkar
        locs = locs.filter((l) => l !== "Genel");
      }
      if (!locs.length) locs = ["Genel"];
      next.locations = locs;
      next.location = locs[0];
    }
    setItems((its) => its.map((it, i) => (i === idx ? next : it)));
    if ("system_id" in patch || "part_id" in patch || "locations" in patch || "location" in patch) {
      setRepeats((r) => ({ ...r, [idx]: null }));
      checkRepeat(idx, next);
    }
  };

  const setDecision = (idx, candidateKey, decision) => {
    setRepeats((r) => {
      const cur = r[idx] || {};
      return { ...r, [idx]: { ...cur, decisions: { ...(cur.decisions || {}), [candidateKey]: decision } } };
    });
  };

  const addItem = () => setItems((its) => [...its, emptyItem()]);
  const removeItem = (idx) => setItems((its) => (its.length <= 1 ? its : its.filter((_, i) => i !== idx)));

  const pickVehicle = (v) => { setPlate(v.plate || ""); setVehicleCode(v.code || ""); };

  const validate = () => {
    const e = {};
    if (!date) e.date = true;
    if (!plate.trim()) e.plate = true;
    if (!durationMinutes) e.duration = true;
    items.forEach((it, i) => {
      if (!it.job_level) e[`item_${i}_level`] = true;
      if (!it.primary_mechanic_id) e[`item_${i}_mech`] = true;
      if (!it.system_id) e[`item_${i}_system`] = true;
      const locs = (it.locations && it.locations.length ? it.locations : [it.location]);
      if (!locs || !locs.length || !locs[0]) e[`item_${i}_location`] = true;
      // Update 29 — her uyarı için ayrı karar
      const w = repeats[i];
      if (w && Array.isArray(w.candidates) && w.candidates.length) {
        const dec = w.decisions || {};
        for (const c of w.candidates) {
          const k = `${c.work_order_id}::${c.item_id}`;
          if (!dec[k]) { e[`item_${i}_decision`] = true; break; }
        }
      }
    });
    setErrors(e);
    if (Object.keys(e).length) {
      const decMissing = Object.keys(e).some((k) => k.endsWith("_decision"));
      toast.error(decMissing
        ? "Tekrar arıza uyarısı için 'Evet' veya 'Hayır' seçmelisiniz"
        : "Eksik alanlar var — kırmızı işaretli kısımları doldurun");
    }
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const { start_time, end_time } = buildTimeWindow(date, durationMinutes);
      const payload = {
        date: `${date}T09:00:00`,
        items: items.map((it) => {
          const sys = systems.find((s) => s.id === it.system_id);
          const part = sys?.parts?.find((p) => p.id === it.part_id);
          return {
            item_id: it.item_id || undefined,
            job_level: it.job_level,
            primary_mechanic_id: it.primary_mechanic_id,
            secondary_mechanic_id: it.secondary_mechanic_id || null,
            fault_reported: it.fault_reported,
            action_taken: it.action_taken,
            repair_status: it.repair_status,
            custom_points: it.job_level === "Diğer" && it.custom_points !== "" ? parseFloat(it.custom_points) : null,
            system_id: it.system_id || null,
            system_name: sys?.name || it.system_name || null,
            part_id: it.part_id || null,
            part_name: part?.name || it.part_name || null,
            location: (it.locations && it.locations.length ? it.locations[0] : (it.location || "Genel")),
            locations: (it.locations && it.locations.length ? it.locations : [it.location || "Genel"]),
          };
        }),
        vehicle_plate: plate.trim(),
        vehicle_code: vehicleCode.trim(),
        vehicle_km: 0,
        job_levels: [],
        overtime,
        outsourced: false,
        delay_reason: "Gecikme Yok",
        start_time, end_time,
        fault_reported: "",
        action_taken: "",
        parts_list: parts.filter((p) => p.name.trim()).map((p) => ({ name: p.name.trim(), quantity: Number(p.quantity) || 1, note: p.note || "" })),
        entered_by: "form",
        repair_status: "Başarılı Onarım",
        photo_paths: [],
        breakdown_or_overtime: overtime,
        driver_fault: driverFault,
        duration_bucket: durationBucket,
      };
      const { data: saved } = isEdit
        ? await api.put(`/work-orders/${id}`, payload)
        : await api.post("/work-orders", payload);
      // Her kalem için karar bloklarını batch endpoint'e gönder
      for (let i = 0; i < items.length; i++) {
        const w = repeats[i];
        if (!w || !Array.isArray(w.candidates) || !w.candidates.length) continue;
        const decisions = [];
        for (const c of w.candidates) {
          const k = `${c.work_order_id}::${c.item_id}`;
          const d = (w.decisions || {})[k];
          if (!d) continue;
          decisions.push({
            decision: d,
            previous_work_order_id: d === "yes" ? c.work_order_id : null,
            previous_item_id: d === "yes" ? c.item_id : null,
            severity: c.severity || w.severity,
            matched_locations: c.matched_locations || [],
          });
        }
        if (!decisions.length) continue;
        const savedItem = (saved.items || [])[i];
        if (!savedItem?.item_id) continue;
        try {
          await api.post(`/work-orders/${saved.id}/items/${savedItem.item_id}/repeat-decisions`, { decisions });
        } catch (er) { /* silent */ }
      }
      toast.success(isEdit ? "İş emri güncellendi" : "İş emri kaydedildi");
      nav("/is-emirleri");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Kayıt başarısız");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6 pb-[180px] sm:pb-6" data-testid="data-entry-form">
      <style>{`.shake-once{animation:shk .25s 2}@keyframes shk{0%,100%{transform:translateX(0)}50%{transform:translateX(-4px)}}`}</style>

      <header className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-mono">Yeni İş Emri</div>
          <h1 className="mt-1 font-heading font-bold text-3xl lg:text-4xl tracking-tight text-slate-900">
            {isEdit ? "İş Emri Düzenle" : "Bakım / Onarım Formu"}
          </h1>
          <p className="mt-1 text-sm text-slate-500">4 adım: Tarih & Mesai → Araç → Arıza (Sistem/Parça/Konum) → Süre & Parça</p>
        </div>
        <ExternalServiceButton />
      </header>

      {/* 1) Mesai + Tarih */}
      <Card className="border-slate-200">
        <CardContent className="p-5 sm:p-6 space-y-5">
          <SectionLabel step={1} icon={<Clock className="w-4 h-4" />} title="Mesai Durumu & İşlem Tarihi" />
          <div className="grid grid-cols-2 gap-3">
            <BigToggle active={!overtime} onClick={() => setOvertime(false)}
              title="Normal Mesai İçi" subtitle="Havuz kesintisi yok" icon={<Clock className="w-6 h-6" />}
              activeColor="bg-emerald-600 text-white border-emerald-600" testid="overtime-off" />
            <BigToggle active={overtime} onClick={() => setOvertime(true)}
              title="Mesaiye Kaldı" subtitle="Havuz kesintisi olur" icon={<AlertTriangle className="w-6 h-6" />}
              activeColor="bg-amber-600 text-white border-amber-600" testid="overtime-on" />
          </div>
          {overtime && (
            <label className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 rounded-lg p-3 cursor-pointer">
              <input type="checkbox" checked={driverFault} onChange={(e) => setDriverFault(e.target.checked)} data-testid="driver-fault" />
              <span>Şoför hatası (mesaiye kalınsa da havuzdan düşülmez)</span>
            </label>
          )}
          <div className={`transition ${errors.date ? clsRed : ""} rounded-lg`}>
            <Label className="text-xs uppercase tracking-wider text-slate-600 mb-1 block">İşlem Tarihi</Label>
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-slate-400" />
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="max-w-[220px] h-11 text-base" data-testid="date-input" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 2) Araç */}
      <Card className="border-slate-200">
        <CardContent className="p-5 sm:p-6 space-y-4">
          <SectionLabel step={2} icon={<Bus className="w-4 h-4" />} title="Araç" />
          <div className={`transition ${errors.plate ? clsRed : ""} rounded-lg`}>
            <Label className="text-xs uppercase tracking-wider text-slate-600 mb-1 block">Araç Seç veya Plakayı Yaz</Label>
            <div className="flex flex-wrap gap-2 items-center">
              <select value={plate} onChange={(e) => {
                const v = vehicles.find((x) => x.plate === e.target.value);
                if (v) pickVehicle(v); else setPlate(e.target.value);
              }} className="h-11 px-3 rounded-lg border border-slate-300 bg-white text-base font-mono min-w-[220px]" data-testid="plate-select">
                <option value="">— Filodan araç seç —</option>
                {vehicles.map((v) => <option key={v.id} value={v.plate}>{v.code || "?"} — {v.plate}{v.brand ? ` — ${v.brand}` : ""}</option>)}
              </select>
              <span className="text-slate-400">veya</span>
              <Input value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} placeholder="16 BGY 277" className="max-w-[200px] h-11 font-mono text-base" data-testid="plate-input" />
              <Input value={vehicleCode} onChange={(e) => setVehicleCode(e.target.value)} placeholder="Kod" className="max-w-[100px] h-11 font-mono text-base" data-testid="code-input" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 3) Arızalar */}
      <Card className="border-slate-200">
        <CardContent className="p-5 sm:p-6 space-y-5">
          <div className="flex items-center justify-between">
            <SectionLabel step={3} icon={<Wrench className="w-4 h-4" />} title="Yapılan İş(ler): Sistem → Parça → Konum" />
            <Button size="sm" variant="outline" onClick={addItem} data-testid="add-item"><Plus className="w-4 h-4 mr-1" /> Arıza Ekle</Button>
          </div>
          {items.map((it, idx) => (
            <ItemCard
              key={idx}
              idx={idx}
              item={it}
              mechanics={mechanics}
              systems={systems}
              locations={locations}
              onUpdate={(patch) => updateItem(idx, patch)}
              onRemove={items.length > 1 ? () => removeItem(idx) : null}
              errors={errors}
              repeatInfo={repeats[idx]}
              onDecide={(k, d) => setDecision(idx, k, d)}
            />
          ))}
        </CardContent>
      </Card>

      {/* 4) Süre & Parça */}
      <Card className="border-slate-200">
        <CardContent className="p-5 sm:p-6 space-y-5">
          <SectionLabel step={4} icon={<Package className="w-4 h-4" />} title="Süre & Kullanılan Parçalar" />
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600 mb-2 block">Süre</Label>
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
              {DURATION_BUCKETS.map((b) => {
                const active = durationBucket === b.label;
                return (
                  <button key={b.label} type="button" onClick={() => { setDurationBucket(b.label); setDurationMinutes(b.minutes); }}
                    className={`h-11 rounded-lg border-2 text-xs font-bold transition ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}
                    data-testid={`dur-${b.label.replace(/\s|\+|\./g, "")}`}>{b.label}</button>
                );
              })}
            </div>
          </div>
          <PartsPicker parts={parts} onChange={setParts} />
        </CardContent>
      </Card>

      {/* Update 29.19 — Mobil "Kaydet" butonu alt navigasyonun ve klavyenin
          üstünde kalsın: alt bar 76px → bottom offset 76px + safe-area;
          z-40 ile mobile-bottom-nav (z-30) üstünde. Safe-area padding
          sadece bottom offset'inde — pb-safe eklenmez (çift sayım olmasın). */}
      <div
        className="fixed bottom-[calc(76px+env(safe-area-inset-bottom))] inset-x-0 sm:static sm:bottom-auto bg-white sm:bg-transparent border-t sm:border-0 border-slate-200 shadow-[0_-8px_20px_-12px_rgba(0,0,0,0.15)] sm:shadow-none px-4 py-3 sm:p-0 flex items-center justify-end gap-3 z-40 sm:z-10"
        data-testid="submit-bar"
      >
        <Button variant="outline" onClick={() => nav("/is-emirleri")} data-testid="cancel-btn">İptal</Button>
        <Button onClick={submit} disabled={saving} className="bg-[#1E3A8A] hover:bg-[#1e40af] text-white h-12 px-6 text-base font-bold flex-1 sm:flex-initial" data-testid="submit-btn">
          {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Save className="w-5 h-5 mr-2" />}
          {saving ? "Kaydediliyor..." : isEdit ? "Güncelle" : "İş Emrini Kaydet"}
        </Button>
      </div>
    </div>
  );
}

// ---------- Alt bileşenler ----------
function SectionLabel({ step, icon, title }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-8 h-8 rounded-full bg-[#1E3A8A] text-white flex items-center justify-center font-bold text-sm">{step}</span>
      <div className="flex items-center gap-2">
        <span className="text-slate-400">{icon}</span>
        <h3 className="font-heading font-bold text-lg text-slate-900">{title}</h3>
      </div>
    </div>
  );
}

function BigToggle({ active, onClick, title, subtitle, icon, activeColor, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`p-5 rounded-2xl border-2 text-left transition-all ${active ? `${activeColor} shadow-lg scale-[1.02]` : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}>
      <div className={`inline-flex items-center gap-2 mb-2 ${active ? "text-white/90" : "text-slate-400"}`}>{icon}</div>
      <div className="font-heading font-bold text-lg leading-tight">{title}</div>
      <div className={`text-xs mt-1 ${active ? "text-white/80" : "text-slate-500"}`}>{subtitle}</div>
    </button>
  );
}

function SearchableSelect({ options, value, onChange, placeholder, testid, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const onClickAway = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, []);
  const selected = options.find((o) => o.id === value);
  const filtered = query
    ? options.filter((o) => (o.name || "").toLowerCase().includes(query.toLowerCase()))
    : options;
  return (
    <div className="relative" ref={ref}>
      <button type="button" disabled={disabled} onClick={() => !disabled && setOpen((v) => !v)}
        className={`w-full h-11 px-3 rounded-lg border border-slate-300 bg-white text-sm text-left flex items-center justify-between ${disabled ? "opacity-50" : ""}`}
        data-testid={testid}>
        <span className={selected ? "" : "text-slate-400"}>{selected?.name || placeholder}</span>
        <ChevronDown className="w-4 h-4 text-slate-400" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-slate-100 flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-400" />
            <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Ara..." className="flex-1 text-sm outline-none" />
          </div>
          <div className="overflow-auto flex-1">
            {filtered.length === 0 && <div className="px-3 py-4 text-xs text-slate-400 text-center">Sonuç yok</div>}
            {filtered.map((o) => (
              <button key={o.id} type="button" onClick={() => { onChange(o.id); setOpen(false); setQuery(""); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${o.id === value ? "bg-blue-100 font-semibold" : ""}`}
                data-testid={`${testid}-opt-${o.id.slice(0, 8)}`}>
                {o.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ItemCard({ idx, item, mechanics, systems, locations, onUpdate, onRemove, errors, repeatInfo, onDecide }) {
  const activeSystems = systems.filter((s) => s.active);
  const currentSystem = systems.find((s) => s.id === item.system_id);
  const parts = currentSystem
    ? (currentSystem.parts || []).filter((p) => p.active || p.id === item.part_id)
    : [];

  return (
    <div className="rounded-2xl border-2 border-slate-200 bg-slate-50/50 p-4 sm:p-5 space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Arıza #{idx + 1}</span>
        {onRemove && (
          <button onClick={onRemove} className="text-rose-600 hover:text-rose-700 text-xs flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Sil</button>
        )}
      </div>

      {/* Sistem → Parça → Konum */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className={`transition ${errors[`item_${idx}_system`] ? clsRed : ""} rounded-lg`}>
          <Label className="text-xs uppercase tracking-wider text-slate-600 mb-2 flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> Sistem</Label>
          <SearchableSelect
            options={activeSystems.map((s) => ({ id: s.id, name: s.name }))}
            value={item.system_id}
            onChange={(sid) => onUpdate({ system_id: sid, part_id: "" })}
            placeholder="— Sistem seç —"
            testid={`sys-${idx}`}
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-600 mb-2 block">Parça</Label>
          <SearchableSelect
            options={parts.map((p) => ({ id: p.id, name: p.name }))}
            value={item.part_id}
            onChange={(pid) => onUpdate({ part_id: pid })}
            placeholder={currentSystem ? "— Parça seç —" : "önce sistem seç"}
            testid={`part-${idx}`}
            disabled={!currentSystem}
          />
        </div>
        <div className={`transition ${errors[`item_${idx}_location`] ? clsRed : ""} rounded-lg`}>
          <Label className="text-xs uppercase tracking-wider text-slate-600 mb-2 block">Konum (çoklu seçim)</Label>
          <div className="flex flex-wrap gap-1.5" data-testid={`loc-chips-${idx}`}>
            {locations.map((l) => {
              const active = (item.locations || [item.location || "Genel"]).includes(l);
              return (
                <button
                  key={l} type="button"
                  onClick={() => {
                    const cur = new Set(item.locations || [item.location || "Genel"]);
                    if (cur.has(l)) cur.delete(l); else cur.add(l);
                    onUpdate({ locations: Array.from(cur) });
                  }}
                  data-testid={`loc-chip-${idx}-${l.replace(/\s+/g,'_')}`}
                  className={`h-9 px-3 rounded-full text-xs font-medium border transition ${
                    active ? (l === "Genel" ? "bg-slate-800 text-white border-slate-800" : "bg-blue-600 text-white border-blue-600")
                          : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"}`}>
                  {l}
                </button>
              );
            })}
          </div>
          <div className="mt-1 text-[10px] text-slate-500">"Genel" başka konumla birlikte seçilemez. En az bir konum zorunlu.</div>
        </div>
      </div>

      {/* Repeat warnings — Update 29: her önceki kalem için ayrı blok */}
      {repeatInfo && Array.isArray(repeatInfo.candidates) && repeatInfo.candidates.length > 0 && (
        <div className="space-y-2" data-testid={`repeat-warnings-${idx}`}>
          {repeatInfo.candidates.map((c) => {
            const k = `${c.work_order_id}::${c.item_id}`;
            const dec = (repeatInfo.decisions || {})[k];
            const strong = c.severity === "strong";
            return (
              <div key={k} className={`border-2 rounded-xl p-4 flex gap-3 items-start ${strong ? "border-red-500 bg-red-50" : "border-amber-500 bg-amber-50"}`}
                data-testid={`repeat-block-${idx}-${(c.item_id || "").slice(0,8)}`}>
                <AlertTriangle className={`w-6 h-6 flex-shrink-0 mt-0.5 ${strong ? "text-red-600" : "text-amber-600"}`} />
                <div className="flex-1 text-sm">
                  <div className={`font-bold mb-1 ${strong ? "text-red-800" : "text-amber-900"}`}>
                    {strong ? "⚠️ GÜÇLÜ" : "⚡ ZAYIF"} — {c.date} · {c.mechanic}
                  </div>
                  <div className="text-slate-700 text-xs">
                    <em>{c.system_name}</em> / <em>{c.part_name || "—"}</em> — Önceki konum(lar): <em>{(c.locations || [c.location]).join(", ")}</em>
                    {c.matched_locations && c.matched_locations.length > 0 && (
                      <span> · <b>Eşleşti:</b> {c.matched_locations.join(", ")}</span>
                    )}
                    <div className="mt-1 italic">"{(c.action || "").slice(0, 100)}"</div>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <Button size="sm" variant={dec === "yes" ? "default" : "outline"}
                      className={dec === "yes" ? "bg-red-600 hover:bg-red-700 text-white" : ""}
                      onClick={() => onDecide(k, "yes")} data-testid={`decide-yes-${idx}-${(c.item_id||"").slice(0,8)}`}>
                      Evet, tekrar
                    </Button>
                    <Button size="sm" variant={dec === "no" ? "default" : "outline"}
                      className={dec === "no" ? "bg-slate-800 hover:bg-slate-900 text-white" : ""}
                      onClick={() => onDecide(k, "no")} data-testid={`decide-no-${idx}-${(c.item_id||"").slice(0,8)}`}>
                      Hayır, farklı
                    </Button>
                    {!dec && errors[`item_${idx}_decision`] && (
                      <span className="text-xs text-red-700 font-semibold">← Karar zorunlu</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Seviye + Usta */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className={`transition ${errors[`item_${idx}_level`] ? clsRed : ""} rounded-lg`}>
          <Label className="text-xs uppercase tracking-wider text-slate-600 mb-2 block">İş Seviyesi</Label>
          <div className="grid grid-cols-3 gap-2">
            {JOB_LEVELS.map((lvl) => {
              const active = item.job_level === lvl;
              return (
                <button key={lvl} type="button" onClick={() => onUpdate({ job_level: lvl })}
                  className={`h-11 rounded-lg border-2 text-xs font-bold transition ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}
                  data-testid={`level-${idx}-${lvl.replace(/[^a-zA-Z]/g, "")}`}>
                  {lvl}
                </button>
              );
            })}
          </div>
          {item.job_level === "Diğer" && (
            <Input type="number" step="1" placeholder="Manuel puan (örn: 30)" value={item.custom_points} onChange={(e) => onUpdate({ custom_points: e.target.value })} className="mt-2 h-11" data-testid={`custom-pts-${idx}`} />
          )}
        </div>
        <div className={`transition ${errors[`item_${idx}_mech`] ? clsRed : ""} rounded-lg`}>
          <Label className="text-xs uppercase tracking-wider text-slate-600 mb-2 flex items-center gap-2"><User className="w-3.5 h-3.5" /> Sorumlu Usta</Label>
          <select value={item.primary_mechanic_id} onChange={(e) => onUpdate({ primary_mechanic_id: e.target.value })}
            className="w-full h-11 px-3 rounded-lg border border-slate-300 bg-white text-base" data-testid={`mech-${idx}`}>
            <option value="">— Usta seç —</option>
            {mechanics.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <select value={item.secondary_mechanic_id} onChange={(e) => onUpdate({ secondary_mechanic_id: e.target.value })}
            className="mt-2 w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-600" data-testid={`mech2-${idx}`}>
            <option value="">(Yardımcı usta yok)</option>
            {mechanics.filter((m) => m.id !== item.primary_mechanic_id).map((m) => <option key={m.id} value={m.id}>{m.name} (yardımcı)</option>)}
          </select>
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-600 mb-1 block">Yapılan İş / Açıklama</Label>
        <Textarea value={item.action_taken} onChange={(e) => onUpdate({ action_taken: e.target.value })} rows={2}
          placeholder="Örn: Ön sağ balata değişti, kliper temizlendi" className="text-sm" data-testid={`action-${idx}`} />
      </div>
    </div>
  );
}

function PartsPicker({ parts, onChange }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return PARTS_CATALOG.slice(0, 12);
    return PARTS_CATALOG.filter((p) => p.toLowerCase().includes(q)).slice(0, 15);
  }, [query]);
  const addPart = (name) => {
    if (!name.trim()) return;
    if (parts.some((p) => p.name.toLowerCase() === name.toLowerCase())) return;
    onChange([...parts, { name: name.trim(), quantity: 1, note: "" }]);
    setQuery(""); setOpen(false); inputRef.current?.focus();
  };
  const updatePart = (i, patch) => onChange(parts.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const removePart = (i) => onChange(parts.filter((_, idx) => idx !== i));
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-600 mb-2 flex items-center gap-2"><Package className="w-3.5 h-3.5" /> Kullanılan Parçalar (Fatura)</Label>
      <div className="relative">
        <div className="flex gap-2">
          <Input ref={inputRef} value={query} onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)} placeholder="Parça ara..." className="h-11" data-testid="parts-search"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPart(query); } }} />
          <Button type="button" onClick={() => addPart(query)} disabled={!query.trim()} variant="outline" className="h-11 whitespace-nowrap"><Plus className="w-4 h-4 mr-1" /> Ekle</Button>
        </div>
        {open && suggestions.length > 0 && (
          <div className="absolute z-20 mt-1 w-full max-h-60 overflow-auto bg-white border border-slate-200 rounded-lg shadow-lg" onMouseLeave={() => setOpen(false)}>
            {suggestions.map((s) => (
              <button key={s} type="button" onClick={() => addPart(s)}
                className="w-full text-left px-3 py-2 hover:bg-slate-100 text-sm flex items-center gap-2 border-b border-slate-100 last:border-0">
                <Sparkles className="w-3 h-3 text-blue-500" /> {s}
              </button>
            ))}
          </div>
        )}
      </div>
      {parts.length > 0 && (
        <div className="mt-3 space-y-2">
          {parts.map((p, i) => (
            <div key={i} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid={`part-row-${i}`}>
              <span className="flex-1 text-sm font-medium">{p.name}</span>
              <Input type="number" min="1" value={p.quantity} onChange={(e) => updatePart(i, { quantity: e.target.value })} className="max-w-[80px] h-9 text-center" />
              <Input value={p.note} onChange={(e) => updatePart(i, { note: e.target.value })} placeholder="not" className="max-w-[220px] h-9 text-xs" />
              <button onClick={() => removePart(i)} className="text-rose-600 hover:text-rose-700 p-1"><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ExternalServiceButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button type="button" variant="outline" onClick={() => setOpen(true)} className="border-orange-400 text-orange-700 hover:bg-orange-50" data-testid="open-external-service-btn">
        <Wrench className="w-4 h-4 mr-2" /> Dış Servis Kaydı
      </Button>
      <ExternalServiceDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

